import eventMixin from './mixins/event.js';
import Logger from './logger.js';

export default class Cart {

	constructor() {
		this.NAME = `Cart`;
		this.cartJson = null;
		this.readGeneration = 0;
		this.publishedGeneration = -1;
		this.pendingRead = null;
		this.pendingMutations = new Set();
	}

	init() {
		Logger.info(`${this.NAME} > init`);
	}

	/**
	 * add - Add items to the cart
	 *
	 * @param {*} items
	 */
	async add(items) {
		Logger.info(`${this.NAME} > add`);

		const self = this;

		// Set up the payload
		let formData = {
			'items': []
		};
		items.forEach((item) => {

			if (!item.id) {
				Logger.error(`${this.NAME} > add > No product id provided. Omitting product from cart add.`)
				return;
			}

			if (!item.quantity) {
				item.quantity = 1;
			}

			formData.items.push({
				'id': item.id,
				'quantity': item.quantity
			});
		});

		try {
			await self.mutate('cart/add.js', formData, 'cart.adding', 'cart.added');
			await self.get();
		} catch (error) {
			console.error('Error:', error);
		}
	}

	/**
	 * change - This function will change an existing line item in the cart.
	 *
	 * This is a good function to use if you have the key of the line item (not the variant ID).
	 *
	 * @param {*} lineItemKey
	 * @param {*} updates
	 * @param {*} attributes
	 */
	async change(lineItemKey, updates, attributes) {
		Logger.info(`${this.NAME} > change`);

		const self = this;

		// Set up the payload
		let formData = {
			id: lineItemKey
		};

		if (updates.quantity != null) {
			formData.quantity = updates.quantity
		}

		if (attributes != null) {
			formData.attributes = attributes;
		}

		await self.mutate('cart/change.js', formData, 'cart.changing', 'cart.changed');
	}

	get(preferCached) {
		Logger.info(`${this.NAME} > get`);

		// Keep the legacy argument, but share only pending work, never a settled cart.
		if (this.pendingRead) return this.pendingRead.promise;
		const read = { generation: this.readGeneration, promise: null };
		this.pendingRead = read;
		read.promise = Promise.resolve().then(async () => {
			try {
				while (this.pendingMutations.size) {
					await Promise.all(this.pendingMutations);
				}
				if (read.generation !== this.readGeneration) return this.followCurrentRead();
				this.trigger('cart.getting');
				if (read.generation !== this.readGeneration) return this.followCurrentRead();

				const cart = await this.requestJson('cart.js', { method: 'GET' });
				if (read.generation !== this.readGeneration) return this.followCurrentRead();
				if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');

				if (cart.items.length) {
					const metadata = await this.getCartMetafields();
					if (read.generation !== this.readGeneration) return this.followCurrentRead();
					const byVariant = new Map(metadata.items.map(item => [String(item.variant_id), item]));
					cart.items.forEach(item => {
						const metafields = byVariant.get(String(item.variant_id));
						if (metafields) item.metafields = metafields;
					});
				}

				this.cartJson = cart;
				this.publishedGeneration = read.generation;
				this.trigger('cart.get', cart);
				return read.generation === this.readGeneration ? cart : this.followCurrentRead();
			} catch (error) {
				// A superseded request (including a failed one) must follow the current read.
				if (read.generation !== this.readGeneration) return this.followCurrentRead();
				throw error;
			} finally {
				if (this.pendingRead === read) this.pendingRead = null;
			}
		});
		return read.promise;
	}

	followCurrentRead() {
		if (this.pendingRead) return this.pendingRead.promise;
		// An obsolete response can arrive after its replacement has already completed.
		// Reuse that replacement for its waiting callers, but never for a new get().
		if (this.publishedGeneration === this.readGeneration) return this.cartJson;
		return this.get();
	}

	async requestJson(path, options) {
		const response = await fetch(window.Shopify.routes.root + path, {
			...options,
			headers: { 'Content-Type': 'application/json' }
		});
		const json = await response.json();
		if (!response.ok) throw new Error(json.description || json.message || `Cart request failed (${response.status})`);
		return json;
	}

	async mutate(path, formData, beforeEvent, afterEvent) {
		let release;
		let failure;
		const pending = new Promise(resolve => { release = resolve; });
		this.pendingMutations.add(pending);
		this.readGeneration++;
		this.pendingRead = null;
		try {
			this.trigger(beforeEvent, formData);
			this.triggerUpdating();
			await this.requestJson(path, { method: 'POST', body: JSON.stringify(formData) });
		} catch (error) {
			failure = error;
		} finally {
			// Invalidate reads on failure too: a rejected response can still change the cart.
			this.readGeneration++;
			this.pendingRead = null;
			this.pendingMutations.delete(pending);
			release();
		}
		try {
			if (failure) this.trigger('cart.error', { error: failure, path, formData });
			else {
				this.trigger(afterEvent, formData);
				this.triggerUpdated();
			}
		} finally {
			// Event handlers can start another write, so check after notifying them.
			if (!this.isUpdating()) this.trigger('cart.settled');
		}
		if (failure) throw failure;
	}

	isUpdating() {
		return this.pendingMutations.size > 0;
	}

	/**
	 * getCartMetafields - Get the cart metafields
	 *
	 * @returns json
	 */
	async getCartMetafields() {
		Logger.info(`${this.NAME} > getCartMetafields`);

		// This template is found in /templates/cart.metafields.json.liquid
		const json = await this.requestJson('cart?view=metafields.json', { method: 'GET' });
		if (!Array.isArray(json.items)) throw new Error('Invalid cart metadata response');
		return json;
	}

	triggerUpdating() {
		this.trigger(`cart.updating`);
	}

	triggerUpdated() {
		this.trigger(`cart.updated`);
	}

	/**
	 * update - Update a variant in the cart
	 *
	 * Ref:
	 * https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-update-js
	 *
	 * @param {*} variantId
	 * @param {*} updates
	 * @param {*} attributes
	 */
	async update(variantId, updates, attributes) {
		Logger.info(`${this.NAME} > change`);

		const self = this;

		// Set up the payload
		let formData = {};

		// Updates should resemble:
		// { variantId: quantity, variantId: quantity}
		if (updates != null) {
			formData.updates = updates;
		}

		if (attributes != null) {
			formData.attributes = attributes;
		}

		await self.mutate('cart/update.js', formData, 'cart.changing', 'cart.changed');
	}
}

// Mixins
Object.assign(Cart.prototype, eventMixin);
